import React, {
	useState,
	useEffect,
	useCallback,
	useRef,
	Fragment as Frag
} from 'react';
import PropTypes from 'prop-types';
import MomentCore from 'moment';
import ReactPlaceholder from 'react-placeholder';
import {TextBlock, RoundShape, RectShape, TextRow} from 'react-placeholder/lib/placeholders';
import InfiniteScroll from 'react-infinite-scroller';
import {
	useAtom
} from 'jotai';

import {
	makeStyles,
	Grid,
	Paper,
	Typography
} from '@material-ui/core';

import {patternsChangedAtom} from "../data/atoms/patterns";
import Constants from "../utils/Constants";
import Fido from "../utils/Fido";
import DeepMerge from "../utils/DeepMerge";
import InfoGraphic from "../assets/patterns-infograpic.svg";
import PaddedLayout from "../components/PaddedLayout";
import Banner from "../components/Banner";
import PatternCard from "../components/PatternCard";
import CircularLoadingIndicator from "../components/CircularLoadingIndicator";

const styles = makeStyles(theme => ({
	paddedLayout: {
		[theme.breakpoints.up('sm')]: {
			paddingTop: theme.spacing(4)
		},
		paddingBottom: theme.spacing(3)
	},

	infoGraphicItem: {
		width: "60%"
	},

	monthHeading: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(1)
	},

	firstMonthHeading: {
		marginTop: 0
	},

	banner: {
		marginBottom: theme.spacing(2)
	},

	bannerContent: {
		"&:last-child": {
			[theme.breakpoints.up('sm')]: {
				paddingTop: theme.spacing(2)
			}
		}
	},

	bannerActions: {
		[theme.breakpoints.up('sm')]: {
			paddingTop: theme.spacing(1)
		}
	}
}));

const placeholderColor = Constants.COLORS.PLACEHOLDER_BG;
const itemPlaceHolder = (
	<Paper style={{padding: 10, marginTop: 10, maxWidth: 700, margin: "0 auto"}}>
		<Grid container justify="space-between">
			<Grid item>
				<TextBlock rows={1} color={placeholderColor} style={{width: 100}} />
			</Grid>
			<Grid item>
				<Grid container alignItems="center">
					<Grid item>
						<TextBlock rows={1} color={placeholderColor} style={{width: 100}} />
					</Grid>
					<Grid item>
						<RoundShape color={placeholderColor} style={{height: 15, width: 15}} />
					</Grid>
				</Grid>
			</Grid>
		</Grid>
		<Grid container
			wrap="nowrap"
			spacing={2}
			style={{marginTop: 10}}>
			<Grid item>
				<RectShape color={placeholderColor} style={{width: 102, height: 56}} />
			</Grid>
			<Grid item>
				<RectShape color={placeholderColor} style={{width: 102, height: 56}} />
			</Grid>
			<Grid item>
				<RectShape color={placeholderColor} style={{width: 102, height: 56}} />
			</Grid>
			<Grid item>
				<RectShape color={placeholderColor} style={{width: 102, height: 56}} />
			</Grid>
		</Grid>
		<RectShape color={placeholderColor} style={{height: 100, marginTop: 10}} />
		<TextRow color={placeholderColor} style={{marginTop: 10, width: "100%"}} />
	</Paper>
);

function PatternsList(props) {
	const bannerRef = useRef();
	const [data, setData] = useState();
	const [groupedData, setGroupedData] = useState({});
	const [isLoadingPage, setIsLoadingPage] = useState(false);
	// only get the value, no need for the setter
	const [hasChanges] = useAtom(patternsChangedAtom);

	let classes = styles(),
		{status, showSnackbar, history} = props,
		cardSizing = {xs:12, sm:12, md:6, lg:6, xl:6};

	const loadData = useCallback((page) => {
		let derby = new Fido();
		(async () => {
			let result = await derby.fetch(`/patterns?status=${status}${page ? `&page=${page}` : ""}`)
				.catch(error => {
					return showSnackbar(error.message);
				});

			// TODO: sometimes return [] to test skeleton loaders and info graphic
			if (result) {
				//setData({items: []});
				setData(prevData => {
					return DeepMerge.merge(prevData || {}, result);
				});

				setIsLoadingPage(false);
			}
		})();
	}, [status, setData, showSnackbar]);

	useEffect(() => {
		setData(null);
		loadData();
	}, [loadData]);

	const cardClicked = (item) => {
		history.push(`/patterns/${item.status}/${item.uuid}`);
	};

	const loadMore = () => {
		setIsLoadingPage(true);
		loadData(++data.lastPage);
	};

	// group by month
	useEffect(() => {
		if (data && data.items) {
			let grouper = (pattern) => {
				if (status === Constants.PATTERN.STATUSES.INACTIVE) {
					// activity not desconstructed yet, so using published_at iso time; also note create moment
					// as UTC (from server) so can show in local date
					let moment = new MomentCore.utc(pattern.archiveDate);
					return moment.format('MMMM YYYY');
				} else {
					return "group";
				}
			};

			let grouped = {};
			data.items.map(item => {
				let group = grouper(item);

				if(!(group in grouped)) {
					grouped[group] = [];
				}

				if (grouped[group].findIndex(p => p.uuid === item.uuid) === -1) {
					grouped[group].push(item);
				}

				return item;
			});

			setGroupedData(grouped);
		}
	}, [data, setGroupedData, status]);

	useEffect(() => {
		Object.keys(groupedData).map((key, idx) => {
			return groupedData[key].map(item => {
				item.libraryTopics = Object.keys(item.counts.topics).map(key2 => {return {name: key2};});
				return item;
			});
		});
	}, [groupedData]);

	return (
		<PaddedLayout
			noTopPad
			isLarge
			className={classes.paddedLayout}>
			<Banner
				show={hasChanges.inactivePatterns && status === Constants.PATTERN.STATUSES.ACTIVE}
				bannerRef={bannerRef}
				//onEntered={onEntered}
				//onExited={onExited}
				className={classes.banner}
				contentClassName={classes.bannerContent}
				actionsClassName={classes.bannerActions}
				content="Some patterns might have also become inactive, which will be shown in the 'Inactive' list."
				actions={[{
					text: "View Inactive",
					color: "primary",
					handler: () => history.push('/patterns/inactive')
				}]} />
			{(data && data.items) ?
				data.items.length ?
					<Frag>
						<InfiniteScroll
							initialLoad={false}
							pageStart={1}
							loader={null}
							loadMore={loadMore}
							hasMore={data.hasMoreItems && !isLoadingPage}>
							{Object.keys(groupedData).map((key, idx) => {
								return (
									<Frag key={idx}>
										{status === Constants.PATTERN.STATUSES.INACTIVE ?
											<Typography
												variant="h6"
												className={`${classes.monthHeading} ${idx === 0 ? classes.firstMonthHeading : ""}`}>{key}</Typography> : null
										}
										<Grid container spacing={3} alignItems='flex-start'>
											{groupedData[key].map(item => {
												item.signals.reverse(); // they are ordered descending
												return (
													<Grid
														item
														key={item.uuid}
														{...cardSizing}
														role="group"
														aria-disabled="false">
														<PatternCard
															item={item}
															clickHandler={cardClicked} />
													</Grid>
												);
											})}
										</Grid>
									</Frag>
								);
							})}
						</InfiniteScroll>
						{isLoadingPage ?
							<CircularLoadingIndicator /> : null}
					</Frag>
					:
					<Grid container
						justify="center"
						alignItems="center">
						<Grid
							item
							role="group"
							aria-disabled="false"
							className={classes.infoGraphicItem}>
							<img src={InfoGraphic} alt="info graphic" />
						</Grid>
					</Grid>
				:
				<Grid container spacing={2}>
					<Grid item {...cardSizing}>
						<ReactPlaceholder
							ready={false}
							customPlaceholder={itemPlaceHolder}>
							<span></span>
						</ReactPlaceholder>
					</Grid>
					<Grid item {...cardSizing}>
						<ReactPlaceholder
							ready={false}
							customPlaceholder={itemPlaceHolder}>
							<span></span>
						</ReactPlaceholder>
					</Grid>
					<Grid item {...cardSizing}>
						<ReactPlaceholder
							ready={false}
							customPlaceholder={itemPlaceHolder}>
							<span></span>
						</ReactPlaceholder>
					</Grid>
				</Grid>
			}
		</PaddedLayout>
	);
}

PatternsList.propTypes = {
	history: PropTypes.object.isRequired,
	status: PropTypes.string.isRequired,
	showSnackbar: PropTypes.func.isRequired

};

export default PatternsList;